/**
 * Gateway credentials card — issue #233 (AuthNet Slice 2).
 *
 * Owner-only entry/rotation of the tenant's Authorize.net credential
 * set (API Login ID, Transaction Key, Signature Key, Public Client
 * Key, sandbox/production mode). Lives on the Payments page behind a
 * PermissionGate for `payments-ops.credentials:write:all`; the server
 * enforces the same permission via the deny-by-default route guard.
 *
 * Secret-handling contract (mirrors the server):
 *   - Transaction Key + Signature Key are WRITE-ONLY. The status the
 *     server returns is masked (last-4 of the API Login ID / Accept.js
 *     key + set/unset flags); the secrets are never echoed and this
 *     component never stores them outside the controlled inputs, which
 *     are cleared on a successful save.
 *   - Set and rotate are the same operation: the full 4-credential set
 *     is required every time, so a rotate can never leave a partial
 *     envelope (in particular, pre-Slice-2 envelopes missing the
 *     Accept.js Public Client Key are healed by a rotate).
 */
import { useCallback, useEffect, useState } from 'react';
import {
  adminGatewayCredentialsApi,
  type AdminGatewayCredentialStatus,
  type AdminGatewayCredentialsInput,
} from '../../lib/api.js';

type GatewayCredentialsCardProps = {
  apiUrl: string;
};

type StatusState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; status: AdminGatewayCredentialStatus };

type SaveState =
  | { kind: 'idle' }
  | { kind: 'saving' }
  | { kind: 'saved' }
  | { kind: 'error'; message: string };

type TestState =
  | { kind: 'idle' }
  | { kind: 'testing' }
  | { kind: 'ok' }
  | { kind: 'simulation' }
  | { kind: 'failed'; message: string };

function describeStatus(status: AdminGatewayCredentialStatus): {
  label: string;
  tone: 'success' | 'pending' | 'warning' | 'neutral';
} {
  switch (status.state) {
    case 'configured':
      return status.publicClientKeySet
        ? { label: 'Configured', tone: 'success' }
        : { label: 'Rotation needed', tone: 'warning' };
    case 'sealed_unreadable':
      return { label: 'Sealed — unreadable', tone: 'warning' };
    case 'not_configured':
      return { label: 'Not configured', tone: 'neutral' };
  }
}

const EMPTY_FORM: AdminGatewayCredentialsInput = {
  apiLoginId: '',
  transactionKey: '',
  signatureKey: '',
  publicClientKey: '',
  // Default to PRODUCTION: a real merchant entering live keys from their
  // Authorize.net account is going live, not sandbox-testing. Saving live
  // keys under 'sandbox' silently routes the test/charge to the sandbox
  // endpoint and fails with "authentication failed" (the #footgun a
  // first owner hit). The mode field below is prominent + the Test
  // connection button catches any mismatch. Rotates still prefill from
  // the sealed status (loadStatus), so this default only affects first entry.
  mode: 'production',
};

export function GatewayCredentialsCard({
  apiUrl,
}: GatewayCredentialsCardProps): JSX.Element {
  const [status, setStatus] = useState<StatusState>({ kind: 'loading' });
  const [save, setSave] = useState<SaveState>({ kind: 'idle' });
  const [form, setForm] = useState<AdminGatewayCredentialsInput>(EMPTY_FORM);
  const [test, setTest] = useState<TestState>({ kind: 'idle' });

  // Validate the SEALED credentials against the live gateway WITHOUT
  // moving money (authenticateTestRequest). Turns the "configured" badge
  // into a real "these keys actually authenticate" check before go-live.
  const runTest = useCallback(async () => {
    setTest({ kind: 'testing' });
    const res = await adminGatewayCredentialsApi.testConnection(apiUrl);
    if (res.error || !res.data) {
      setTest({ kind: 'failed', message: res.error ?? 'Connection test failed' });
      return;
    }
    const c = res.data.connection;
    if (c.simulation) {
      setTest({ kind: 'simulation' });
    } else if (c.verified) {
      setTest({ kind: 'ok' });
    } else {
      setTest({
        kind: 'failed',
        message: 'The gateway did not verify these credentials.',
      });
    }
  }, [apiUrl]);

  const loadStatus = useCallback(async () => {
    setStatus({ kind: 'loading' });
    const result = await adminGatewayCredentialsApi.getStatus(apiUrl);
    if (result.error || !result.data) {
      setStatus({
        kind: 'error',
        message: result.error ?? 'Could not load gateway credential status',
      });
      return;
    }
    const credentials = result.data.credentials;
    setStatus({ kind: 'ready', status: credentials });
    // Review fix (#244 SHOULD-FIX 1): pre-fill the rotate form's mode
    // from the SEALED status. Defaulting a production tenant's rotate
    // form to 'sandbox' would — once #232 activation is live — let an
    // inattentive rotate silently flip the live charge endpoint to
    // sandbox (fake approvals on a real tenant). Safe to set here: the
    // form only renders when status is 'ready', and loadStatus runs on
    // mount / from the error-state Retry button, never mid-edit.
    if (credentials.state === 'configured') {
      setForm((f) => ({ ...f, mode: credentials.mode }));
    }
  }, [apiUrl]);

  useEffect(() => {
    void loadStatus();
  }, [loadStatus]);

  const setField = useCallback(
    <K extends keyof AdminGatewayCredentialsInput>(
      key: K,
      value: AdminGatewayCredentialsInput[K],
    ) => {
      setForm((f) => ({ ...f, [key]: value }));
      setSave((s) => (s.kind === 'saved' || s.kind === 'error' ? { kind: 'idle' } : s));
    },
    [],
  );

  const handleSubmit = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      const missing =
        form.apiLoginId.trim().length === 0 ||
        form.transactionKey.trim().length === 0 ||
        form.signatureKey.trim().length === 0 ||
        form.publicClientKey.trim().length === 0;
      if (missing) {
        setSave({
          kind: 'error',
          message:
            'All four credentials are required — a rotation always replaces the full set.',
        });
        return;
      }
      setSave({ kind: 'saving' });
      const result = await adminGatewayCredentialsApi.update(apiUrl, {
        apiLoginId: form.apiLoginId.trim(),
        transactionKey: form.transactionKey.trim(),
        signatureKey: form.signatureKey.trim(),
        publicClientKey: form.publicClientKey.trim(),
        mode: form.mode,
      });
      if (result.error || !result.data) {
        setSave({
          kind: 'error',
          message: result.error ?? 'Could not save the credentials',
        });
        return;
      }
      // Write-only secrets: wipe the inputs the moment the seal lands.
      // Keep `mode` aligned with the freshly-sealed status (NOT the
      // EMPTY_FORM sandbox default) — same dangerous-default rationale
      // as the loadStatus pre-fill above.
      const fresh = result.data.credentials;
      setForm({
        ...EMPTY_FORM,
        mode: fresh.state === 'configured' ? fresh.mode : form.mode,
      });
      setSave({ kind: 'saved' });
      setStatus({ kind: 'ready', status: fresh });
    },
    [apiUrl, form],
  );

  return (
    <article
      className="ferro-card"
      aria-label="Payment gateway credentials"
      data-testid="gateway-creds-card"
    >
      <header className="ferro-card__header">
        <h2 className="ferro-section__subtitle">Authorize.net credentials</h2>
        {status.kind === 'ready' ? (
          <span
            className={`ferro-badge ferro-badge--${describeStatus(status.status).tone}`}
            data-testid="gateway-creds-badge"
          >
            {describeStatus(status.status).label}
          </span>
        ) : null}
      </header>

      <p className="ferro-muted">
        Your gateway credentials come from the Authorize.net merchant
        interface (Account → Settings → API Credentials &amp; Keys, and
        Manage Public Client Key). They are encrypted at rest and{' '}
        <strong>never shown again</strong> after saving — rotating always
        replaces the full set.
      </p>

      {status.kind === 'loading' ? (
        <p className="ferro-muted" role="status">
          Loading credential status…
        </p>
      ) : null}

      {status.kind === 'error' ? (
        <div className="ferro-dashboard__error" role="alert">
          <p>{status.message}</p>
          <button
            type="button"
            className="ferro-btn ferro-btn--ghost"
            onClick={() => void loadStatus()}
          >
            Retry
          </button>
        </div>
      ) : null}

      {status.kind === 'ready' ? (
        <>
          {status.status.state === 'configured' ? (
            <>
            <dl
              className="ferro-intake__statusGrid"
              data-testid="gateway-creds-status-grid"
            >
              <div>
                <dt>Mode</dt>
                <dd data-testid="gateway-creds-mode">
                  {status.status.mode === 'production' ? 'Production' : 'Sandbox'}
                </dd>
              </div>
              <div>
                <dt>API Login ID</dt>
                <dd data-testid="gateway-creds-login-last4">
                  <code>•••• {status.status.apiLoginIdLast4}</code>
                </dd>
              </div>
              <div>
                <dt>Transaction Key</dt>
                <dd>
                  <span className="ferro-muted">Sealed — write-only</span>
                </dd>
              </div>
              <div>
                <dt>Signature Key</dt>
                <dd>
                  <span className="ferro-muted">Sealed — write-only</span>
                </dd>
              </div>
              <div>
                <dt>Public Client Key (Accept.js)</dt>
                <dd data-testid="gateway-creds-public-key">
                  {status.status.publicClientKeySet ? (
                    <code>•••• {status.status.publicClientKeyLast4}</code>
                  ) : (
                    <span className="ferro-muted">
                      Missing — re-enter the full set to enable online card
                      entry
                    </span>
                  )}
                </dd>
              </div>
            </dl>
            <div className="ferro-table__actions">
              <button
                type="button"
                className="ferro-btn ferro-btn--outline"
                disabled={test.kind === 'testing'}
                onClick={() => void runTest()}
                data-testid="gateway-test-connection"
              >
                {test.kind === 'testing' ? 'Testing…' : 'Test connection'}
              </button>
            </div>
            {test.kind === 'ok' ? (
              <div
                className="ferro-banner ferro-banner--success"
                role="status"
                data-testid="gateway-test-ok"
              >
                <p>
                  ✓ Connected to Authorize.net — these credentials
                  authenticate. <strong>No charge was made.</strong>
                </p>
              </div>
            ) : null}
            {test.kind === 'simulation' ? (
              <p className="ferro-muted" data-testid="gateway-test-sim">
                Simulation mode — there is no live gateway to test yet.
              </p>
            ) : null}
            {test.kind === 'failed' ? (
              <div
                className="ferro-dashboard__error"
                role="alert"
                data-testid="gateway-test-failed"
              >
                <p>{test.message}</p>
              </div>
            ) : null}
            </>
          ) : null}

          {status.status.state === 'sealed_unreadable' ? (
            <div className="ferro-dashboard__error" role="alert">
              <p>
                Credentials are stored but cannot be read in this environment.
                If this persists, contact support before re-entering them.
              </p>
            </div>
          ) : null}

          <form
            className="ferro-form"
            onSubmit={(e) => void handleSubmit(e)}
            autoComplete="off"
            data-testid="gateway-creds-form"
          >
            <h3 className="ferro-section__subtitle">
              {status.status.state === 'configured'
                ? 'Rotate credentials'
                : 'Enter credentials'}
            </h3>

            <label className="ferro-field">
              <span className="ferro-field__label">API Login ID</span>
              <input
                className="ferro-field__input"
                type="text"
                autoComplete="off"
                value={form.apiLoginId}
                onChange={(e) => setField('apiLoginId', e.target.value)}
                data-testid="gateway-creds-input-login"
              />
            </label>

            <label className="ferro-field">
              <span className="ferro-field__label">Transaction Key</span>
              <input
                className="ferro-field__input"
                type="password"
                autoComplete="new-password"
                value={form.transactionKey}
                onChange={(e) => setField('transactionKey', e.target.value)}
                data-testid="gateway-creds-input-transaction"
              />
            </label>

            <label className="ferro-field">
              <span className="ferro-field__label">Signature Key</span>
              <input
                className="ferro-field__input"
                type="password"
                autoComplete="new-password"
                value={form.signatureKey}
                onChange={(e) => setField('signatureKey', e.target.value)}
                data-testid="gateway-creds-input-signature"
              />
            </label>

            <label className="ferro-field">
              <span className="ferro-field__label">
                Public Client Key (Accept.js)
              </span>
              <input
                className="ferro-field__input"
                type="text"
                autoComplete="off"
                value={form.publicClientKey}
                onChange={(e) => setField('publicClientKey', e.target.value)}
                data-testid="gateway-creds-input-public"
              />
            </label>

            <label className="ferro-field">
              <span className="ferro-field__label">
                Mode — match this to where your keys came from
              </span>
              <select
                className="ferro-field__input"
                value={form.mode}
                onChange={(e) =>
                  setField(
                    'mode',
                    e.target.value === 'production' ? 'production' : 'sandbox',
                  )
                }
                data-testid="gateway-creds-input-mode"
              >
                <option value="production">
                  Production (live keys — real charges)
                </option>
                <option value="sandbox">Sandbox (test keys only)</option>
              </select>
              <span
                className={
                  form.mode === 'production'
                    ? 'ferro-badge ferro-badge--warning'
                    : 'ferro-muted'
                }
                data-testid="gateway-creds-mode-hint"
              >
                {form.mode === 'production'
                  ? '⚠️ Production — your live Authorize.net keys; real cards will be charged.'
                  : 'Sandbox — developer test keys only; no real money moves.'}
              </span>
            </label>

            {save.kind === 'error' ? (
              <p className="ferro-form__error" role="alert">
                {save.message}
              </p>
            ) : null}
            {save.kind === 'saved' ? (
              <p
                className="ferro-form__success"
                role="status"
                data-testid="gateway-creds-saved"
              >
                Credentials sealed. Secrets are write-only — they will not be
                shown again.
              </p>
            ) : null}

            <div className="ferro-form__actions">
              <button
                type="submit"
                className="ferro-btn ferro-btn--primary"
                disabled={save.kind === 'saving'}
                data-testid="gateway-creds-save"
              >
                {save.kind === 'saving'
                  ? 'Sealing…'
                  : status.status.state === 'configured'
                    ? 'Rotate credentials'
                    : 'Save credentials'}
              </button>
            </div>
          </form>
        </>
      ) : null}
    </article>
  );
}
